// useQuickChat — minimal voice-assistant-style chat hook for the
// mobile FAB. Owns its OWN session ID (persisted in localStorage so
// the "Quick" thread persists across visits) and listens to global
// SSE events filtered by "do we have a pending turn?" so it doesn't
// fight with the main `useChat` hook running in the chat tab.
//
// Scope (v2 — turns stack):
//   - turns[] holds the active conversation in the bubble. Each turn
//     has user text + assistant text + tool calls + pending flag.
//   - sendMessage APPENDS a new pending turn (instead of overwriting).
//     Follow-ups stack on top of the previous Q+A so the conversation
//     reads naturally inside the bubble.
//   - reset() clears the local view (the server-side thread persists).
//   - tool_call / tool_result events are handled and attached to the
//     currently-pending turn so the bubble can render tool chips
//     similar to the main chat's "Used <tool>" badges.
//   - reasoning events are dropped (the bubble is a voice-assistant
//     surface, not a full chat — no chain-of-thought stream). We
//     also pass `noThinking: true` to runStart so models that
//     respect it skip thinking entirely.

import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { api, type SessionMessage } from "./rpc";
import { sse, type AgentEvent } from "./sse";

const STORAGE_KEY = "vulture.quick.sessionId";

function loadOrMintSession(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && typeof stored === "string" && stored.length > 0) return stored;
  } catch { /* private mode etc. */ }
  const fresh = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : "s-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  try { localStorage.setItem(STORAGE_KEY, fresh); }
  catch { /* swallow */ }
  return fresh;
}

export type QuickChatState = "idle" | "sending" | "streaming";

export interface QuickChatToolCall {
  id: string;
  name: string;
  args: unknown;
  result: string | null;
  denied?: boolean;
  error?: string;
}

export interface QuickChatTurn {
  /** The user's message that kicked this turn off. */
  user: string;
  /** Streaming/final assistant reply. Grows as tokens arrive. */
  assistant: string;
  /** Tool calls fired during this turn (each gets a result when one arrives). */
  toolCalls: QuickChatToolCall[];
  /** True while the model is still producing tokens for this turn. */
  pending: boolean;
}

export interface UseQuickChat {
  state: QuickChatState;
  /** All turns in this bubble session. Empty until the user has sent
   *  at least one message; reset() clears it. Server-side thread
   *  persists regardless. */
  turns: QuickChatTurn[];
  /** Send a message — appends a new pending turn. Resolves when the
   *  API call returns; the streaming reply continues to update the
   *  appended turn via SSE until agent:done arrives. */
  sendMessage: (text: string) => Promise<void>;
  /** Reset the local view (drops all turns). Server-side thread keeps
   *  its full history; the next sendMessage continues the same thread. */
  reset: () => void;
  /** Session ID of the persistent Quick thread. */
  sessionId: string;
}

export function useQuickChat(): UseQuickChat {
  const sessionIdRef = useRef<string>("");
  if (!sessionIdRef.current) sessionIdRef.current = loadOrMintSession();
  const sessionId = sessionIdRef.current;

  const [state, setState] = useState<QuickChatState>("idle");
  const [turns, setTurns] = useState<QuickChatTurn[]>([]);

  // Mirror turns in a ref so the SSE listener (registered once) sees
  // the latest value without re-subscribing on every state change.
  const turnsRef = useRef<QuickChatTurn[]>([]);
  useEffect(() => { turnsRef.current = turns; }, [turns]);

  // Helper: update the trailing pending turn (which is the one
  // currently receiving tokens / tool calls).
  const updateLastPendingTurn = (mut: (t: QuickChatTurn) => QuickChatTurn) => {
    setTurns((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (!last.pending) return prev;
      return [...prev.slice(0, -1), mut(last)];
    });
  };

  useEffect(() => {
    return sse.on((ev: AgentEvent) => {
      const ts = turnsRef.current;
      if (ts.length === 0) return;
      const last = ts[ts.length - 1];
      if (!last.pending) return;

      if (ev.type === "token" && typeof (ev as any).text === "string") {
        const tokText = (ev as any).text as string;
        updateLastPendingTurn((t) => ({ ...t, assistant: t.assistant + tokText }));
        setState("streaming");
        return;
      }
      if (ev.type === "tool_call") {
        const c = ev as any;
        updateLastPendingTurn((t) => ({
          ...t,
          toolCalls: [
            ...t.toolCalls,
            { id: c.id, name: c.name, args: c.args, result: null },
          ],
        }));
        return;
      }
      if (ev.type === "tool_result") {
        const r = ev as any;
        // Reset the assistant text on every tool result. Voice flow only
        // wants to TTS the FINAL reply — anything the model said
        // *before* this tool ran was narration of its process
        // ("Uploading the pool event…", "Canceling…", "Let me check…")
        // and gets re-rendered into oblivion as soon as the next text
        // chunk arrives. Without this, agent:done fires with a buffer
        // containing every iteration's narration concatenated, and the
        // user hears the whole monologue read aloud through TTS even
        // though the prompt explicitly forbade it. The TTS sentence
        // extractor (in quick-chat.tsx) waits for pending=false before
        // synthesizing, so the user never hears the intermediate text
        // — only the final reply that arrives AFTER the last tool
        // result lands. Bubble shows the same; intermediate narration
        // is visible briefly during the tool call, then replaced.
        updateLastPendingTurn((t) => ({
          ...t,
          assistant: "",
          toolCalls: t.toolCalls.map((c) =>
            c.id === r.id
              ? { ...c, result: typeof r.result === "string" ? r.result : "", denied: !!r.denied, error: r.error || undefined }
              : c,
          ),
        }));
        return;
      }
      if (ev.type === "agent:done") {
        updateLastPendingTurn((t) => ({ ...t, pending: false }));
        setState("idle");
        return;
      }
      // reasoning / approval / ask_user events are deliberately ignored
      // — the bubble is voice-assistant-style and shouldn't expose
      // chain-of-thought or interactive approval flows. Approval-
      // requiring tools are unusual in this path; if they ever fire,
      // the run will hang server-side and the user can dismiss + retry.
    });
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    // Build the conversation array from prior (non-pending) turns BEFORE
    // we append the new turn. The server uses `conversation` as the
    // LLM context — savedSessionId is only for persistence, not for
    // auto-loading prior context. Without this, follow-up questions
    // don't see what the assistant just said.
    const conversation: SessionMessage[] = [];
    for (const t of turnsRef.current) {
      if (t.pending) break;
      conversation.push({ role: "user", content: t.user });
      if (t.assistant) {
        conversation.push({ role: "assistant", content: t.assistant });
      }
    }

    // APPEND a new pending turn so the previous Q+A stays visible.
    setTurns((prev) => [
      ...prev,
      { user: trimmed, assistant: "", toolCalls: [], pending: true },
    ]);
    setState("sending");
    try {
      await api.runStart({
        userMessage: trimmed,
        savedSessionId: sessionId,
        conversation,
        mode: "default",
        // Voice-assistant feel — no exposed reasoning. Models that
        // emit thinking honor this and skip it; models that don't
        // will still produce ordinary tokens we render directly.
        noThinking: true,
        // Voice-mode tool allowlist gate — server strips bash, file,
        // code, memory, generate_image, and other adult-coder tools so
        // the model can't reach for shell to inspect a household list
        // (observed 2026-06-04: `bash` selected on a shopping correction
        // turn because the intent router fell open).
        voiceMode: true,
        // Tight voice-assistant prompt addendum: short replies, no
        // preamble, no filler. Saves both wall-clock (less to
        // generate, less to synthesize aloud) and user attention.
        // Doesn't replace the base system prompt — appended on top.
        systemPromptAppend: [
          "You are currently acting as a voice assistant — your reply will be spoken aloud through TTS and shown briefly in a small popup. Talk like a smart speaker the user just summoned: short, conversational, no lists or formatting unless they explicitly ask for a list.",
          // Tool-call silence rule — bubbled to the top with the role
          // description because the model otherwise narrates every step
          // of multi-tool flows ("Let me check the list, got a 401,
          // retrying with a slug…"). The user hears all of that through
          // TTS and it's noise. Internal failures are NEVER spoken — the
          // tool returns an error, the model decides if/how to retry
          // silently, and reports only the FINAL outcome in one sentence.
          "TOOL-CALL SILENCE — when you call tools, do it silently. NEVER announce \"Let me check / I'll add / Looking up / Trying again\". After tools return, say only the OUTCOME in one short sentence. Never mention HTTP status codes, slugs, IDs, retries, error stack traces, or anything internal. If the tool failed, say what failed in plain language (\"Couldn't find that recipe.\" / \"List is full.\") — never how it failed.",
          // Few-shot anchors covering the household functions (meal plan,
          // calendar, timers, lists, chores, pantry, recipes, to-dos).
          // Tools and date math happen before this turn — these show
          // SPOKEN STYLE, not tool selection. Same string every call →
          // radix-cache friendly. Token cost ~280 amortized over every
          // voice query.
          "Examples of ideal replies (the arrow points at exactly what you say — no preamble, no acknowledgement, no narration of how you got there):\n" +
            "Q: What's for dinner tonight? → A: Chicken parmesan.\n" +
            "Q: What's the meal plan this week? → A: Tonight chicken parmesan, tomorrow taco salad, Thursday pasta primavera.\n" +
            "Q: What's on the calendar today? → A: Two things — soccer at four thirty, dinner out at six.\n" +
            "Q: Anything tomorrow? → A: Nothing scheduled.\n" +
            "Q: Set a ten minute timer for the rice. → A: Ten minutes for the rice, started.\n" +
            "Q: Set a timer. → A: For how long?[continue]\n" +
            "Q: Add dentist next Tuesday at three. → A: Dentist Tuesday at three, added.\n" +
            "Q: Add milk and eggs to the list. → A: Milk and eggs added.\n" +
            "Q: How are the kids doing on chores? → A: Alex is done. Jules has the trash left. Sam hasn't started.\n" +
            "Q: Do we have olive oil? → A: Yes, almost full bottle.\n" +
            "Q: How much flour in the pancake recipe? → A: Two cups.\n" +
            "Q: What's on my to-do list? → A: Nothing right now.\n" +
            // Tool-call-silence demos — recipe save flow that previously
            // had the model narrating 401s + slug retries verbatim.
            "Q: Save this recipe for chicken parmesan. → A: Chicken parmesan saved.\n" +
            "Q: Save a recipe. → A: What's the recipe?[continue]\n" +
            "Q: Schedule an event. → A: For when, and what?[continue]\n" +
            "Q: Add a chore for Jules. → A: Which chore?[continue]",
          "Keep it concise — aim for 1–3 sentences and never more than ~50 words.",
          "CRITICAL: open with the actual answer or outcome. NEVER narrate your process, thinking, or tool calls.",
          "Forbidden openings: \"Let me\", \"I'll\", \"I will\", \"Looking\", \"Checking\", \"Sure\", \"Of course\", \"Got it\", \"Okay\", \"Alright\", \"So,\", \"Well,\", \"It looks like\", \"It seems\", \"Based on\", \"According to\", \"Trying\", \"Attempting\", \"Working on\".",
          "First word must be data — a name, a number, a verb describing the world, not your behavior.",
          "Dates: use ordinal day with month, e.g. \"October fourth\" or \"the fourth\", not \"October 4\".",
          "NEVER use unit abbreviations. Write the full word: \"pound\" / \"pounds\" not \"lb\" / \"lbs\"; \"ounce\" / \"ounces\" not \"oz\"; \"tablespoon\" not \"tbsp\"; \"teaspoon\" not \"tsp\"; \"quart\" not \"qt\"; \"gallon\" not \"gal\"; \"milliliter\" not \"ml\"; \"kilogram\" not \"kg\"; \"inch\" not \"in\"; \"foot\" / \"feet\" not \"ft\".",
          "NEVER use symbols. Write words: \"five dollars\" not \"$5\"; \"fifty percent\" not \"50%\"; \"and\" not \"&\".",
          "ABSOLUTE — no bullet points, no numbered lists, no bold, no headers, no markdown ANY kind. Speak in flowing prose only. If a slot-fill needs multiple pieces of info, ask for them as a short prose question (\"For when, and what?\") not as a list.",
          // Final-position reinforcement — this voice-flow append is the
          // LAST stable text the model sees before the user message, so
          // it's the highest-attention slot we have to keep the
          // [continue] / [end] rules from drifting on multi-turn or
          // post-tool-call generations. Same string every call →
          // radix-cache friendly.
          "ABSOLUTE FINAL RULES — exactly one of three endings on every reply:" +
            " (a) if your reply asks the user anything (a question, a choice, a clarification), end with `[continue]` as the very last characters — no period, no space after." +
            " (b) if the user just dismissed the conversation (\"no thanks\", \"I'm good\", \"that's all\", \"goodbye\", \"thanks\" as a sign-off) and you are POLITELY ACKNOWLEDGING THE CLOSE (\"You're welcome\", \"Anytime\", \"Goodbye\"), end with `[end]` — no period, no space after. Triggers the kiosk to dismiss instead of listening for more." +
            " (c) otherwise (you're stating a fact, confirming an action, delivering data) end with normal punctuation — NO marker. The kiosk will append \"Anything else?\" itself and re-open the mic.",
          "[end] examples (the arrow is what you say):\n" +
            "Q (after \"add milk\"): No thanks I'm good. → A: You're welcome.[end]\n" +
            "Q (after \"what's for dinner\"): That's all bye. → A: Goodbye.[end]\n" +
            "Q (after a recipe save): Nothing else thanks. → A: Anytime.[end]",
        ].join(" "),
      });
    } catch (err) {
      updateLastPendingTurn((t) => ({
        ...t,
        assistant: t.assistant || `Error: ${(err as Error)?.message || "send failed"}`,
        pending: false,
      }));
      setState("idle");
    }
  }, [sessionId]);

  const reset = useCallback(() => {
    setTurns([]);
    setState("idle");
  }, []);

  return { state, turns, sendMessage, reset, sessionId };
}
